import {
  deriveResultLabel,
  resolveResultLabelDisplay,
} from '../safetyCaseClosure/safetyCaseClosure';
import { resolveConfiguredZoneLabel } from '../customerLabels/customerFacingControlLabel';
import type { SafetyCaseRecord } from '../../services/reportsApi';
import type { WorkflowImpactSummary } from '../../services/reportsApi';
import { formatObservedAcrossScans } from './workplaceCondition';

export type WorkplaceConditionContextRow = {
  key: string;
  label: string;
  value: string;
};

export type WorkplaceConditionContext = {
  zone: string | null;
  scanWindow: string | null;
  recurrence: string | null;
  peakTime: string | null;
  correctiveActionStatus: string | null;
  followUpResult: string | null;
  rows: WorkplaceConditionContextRow[];
};

export type WorkplaceConditionContextSource = {
  zoneLabel?: string | null;
  zoneId?: string | null;
  areaLabel?: string | null;
  scope?: Record<string, unknown> | null;
  timeWindow?: { start?: unknown; end?: unknown } | Record<string, unknown> | null;
  linkedReportCount?: number | null;
  logicalScanCount?: number | null;
  workflowSummary?: Record<string, unknown> | null;
  statusSnapshot?: Record<string, unknown> | null;
  correctiveResponseStatus?: string | null;
  safetyCaseStatus?: string | null;
  measurementStatus?: string | null;
  followupMeasurement?: Record<string, unknown> | null;
  peakPeriod?: string | null;
  recurrenceSignalCount?: number | null;
  recurrenceEventCount?: number | null;
  correctiveActionStatusText?: string | null;
};

const CORRECTIVE_ACTION_STATUS_LABELS: Record<string, string> = {
  validated: 'Validated',
  completed: 'Completed',
  started: 'In progress',
  planned: 'Planned',
  waived: 'Waived',
  cancelled: 'Cancelled',
  not_started: 'Not started',
};

function normalizeToken(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function humanizeToken(value: unknown, fallback = 'Not recorded'): string {
  const text = String(value || '').trim();
  if (!text) return fallback;
  return text.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function toCount(value: unknown): number | null {
  const count = Number(value);
  if (!Number.isFinite(count) || count < 0) return null;
  return count;
}

function formatDateTime(value: unknown): string | null {
  const text = String(value || '').trim();
  if (!text) return null;
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return text;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
}

export function formatWorkplaceConditionScanWindow(
  timeWindow?: WorkplaceConditionContextSource['timeWindow'],
): string | null {
  if (!timeWindow || typeof timeWindow !== 'object') return null;
  const start = formatDateTime((timeWindow as Record<string, unknown>).start);
  const end = formatDateTime((timeWindow as Record<string, unknown>).end);
  if (start && end) return `${start} to ${end}`;
  return start || end || null;
}

export function resolveWorkplaceConditionZone(
  source: WorkplaceConditionContextSource,
): string | null {
  const asBuyerLabel = (value: string): string => {
    if (/\s/.test(value)) return value;
    return resolveConfiguredZoneLabel(value) || value;
  };
  const zoneLabel = String(source.zoneLabel || '').trim();
  if (zoneLabel) return asBuyerLabel(zoneLabel);
  const areaLabel = String(source.areaLabel || '').trim();
  if (areaLabel) return asBuyerLabel(areaLabel);

  const scope = source.scope && typeof source.scope === 'object' ? source.scope : {};
  const zones = Array.isArray(scope.zones) ? scope.zones : [];
  for (const zone of zones) {
    const label = String(zone || '').trim();
    if (label) return asBuyerLabel(label);
  }

  const zoneId = String(scope.zone_id || source.zoneId || '').trim();
  if (!zoneId) return null;
  return asBuyerLabel(zoneId);
}

function listCorrectiveActions(
  workflowSummary?: Record<string, unknown> | null,
): Array<Record<string, unknown>> {
  const summaryActions = workflowSummary?.corrective_actions;
  if (!summaryActions || typeof summaryActions !== 'object') return [];
  const actions = summaryActions as Record<string, unknown>;
  return [
    ...((actions.active_actions as Array<Record<string, unknown>> | undefined) || []),
    ...((actions.completed_actions as Array<Record<string, unknown>> | undefined) || []),
    ...((actions.cancelled_actions as Array<Record<string, unknown>> | undefined) || []),
  ];
}

export function resolveWorkplaceConditionRecurrence(
  source: WorkplaceConditionContextSource,
): string | null {
  const recurrenceSignals = toCount(source.recurrenceSignalCount);
  if (recurrenceSignals != null && recurrenceSignals > 0) {
    return recurrenceSignals === 1
      ? '1 recurrence signal in lookback'
      : `${recurrenceSignals} recurrence signals in lookback`;
  }

  const scanCount = toCount(source.logicalScanCount);
  const linkedReports = (scanCount != null && scanCount > 0)
    ? scanCount
    : toCount(source.linkedReportCount);
  if (linkedReports != null && linkedReports > 0) {
    return formatObservedAcrossScans(linkedReports);
  }

  const snapshot = source.statusSnapshot && typeof source.statusSnapshot === 'object'
    ? source.statusSnapshot
    : {};
  const groupedCount = toCount(snapshot.grouped_incident_count);
  if (groupedCount != null && groupedCount > 1) {
    return `${groupedCount} grouped incidents`;
  }

  const evidenceSummary = (
    snapshot.customer_evidence_summary && typeof snapshot.customer_evidence_summary === 'object'
      ? snapshot.customer_evidence_summary
      : {}
  ) as Record<string, unknown>;
  const reportCounts = (
    evidenceSummary.report_counts && typeof evidenceSummary.report_counts === 'object'
      ? evidenceSummary.report_counts
      : {}
  ) as Record<string, unknown>;
  const lookbackSignals = toCount(reportCounts.related_recurrence_signals_lookback);
  if (lookbackSignals != null && lookbackSignals > 0) {
    return lookbackSignals === 1
      ? '1 recurrence signal in lookback'
      : `${lookbackSignals} recurrence signals in lookback`;
  }

  const eventCount = toCount(source.recurrenceEventCount);
  if (eventCount != null && eventCount > 0) {
    return eventCount === 1
      ? '1 observed signal in scan window'
      : `${eventCount} observed signals in scan window`;
  }

  return null;
}

export function resolveWorkplaceConditionPeakTime(
  source: WorkplaceConditionContextSource,
): string | null {
  const peakPeriod = String(source.peakPeriod || '').trim();
  if (peakPeriod) return peakPeriod;

  const snapshot = source.statusSnapshot && typeof source.statusSnapshot === 'object'
    ? source.statusSnapshot
    : {};
  const buyerFinding = (
    snapshot.buyer_executive_finding && typeof snapshot.buyer_executive_finding === 'object'
      ? snapshot.buyer_executive_finding
      : {}
  ) as Record<string, unknown>;
  const snapshotPeak = String(buyerFinding.peak_period || '').trim();
  return snapshotPeak || null;
}

export function resolveWorkplaceConditionCorrectiveActionStatus(
  source: WorkplaceConditionContextSource,
): string | null {
  const explicitText = String(source.correctiveActionStatusText || '').trim();
  if (explicitText) return explicitText;

  const workflowStatus = source.workflowSummary?.corrective_action_status;
  if (workflowStatus && typeof workflowStatus === 'object') {
    const statusLabel = String((workflowStatus as Record<string, unknown>).status_label || '').trim();
    if (statusLabel) return statusLabel;
    const status = normalizeToken((workflowStatus as Record<string, unknown>).status);
    if (status && CORRECTIVE_ACTION_STATUS_LABELS[status]) {
      return CORRECTIVE_ACTION_STATUS_LABELS[status];
    }
    if (status) return humanizeToken(status);
  }

  const correctiveResponse = String(source.correctiveResponseStatus || '').trim();
  if (correctiveResponse) return humanizeToken(correctiveResponse);

  const actions = listCorrectiveActions(source.workflowSummary);
  if (!actions.length) return null;

  const activeCount = actions.filter((action) => {
    const status = normalizeToken(action.status);
    return status && !['completed', 'validated', 'cancelled', 'waived'].includes(status);
  }).length;
  const completedCount = actions.filter((action) => {
    const status = normalizeToken(action.status);
    return ['completed', 'validated'].includes(status);
  }).length;

  if (completedCount > 0 && activeCount === 0) {
    return completedCount === 1 ? '1 completed corrective action' : `${completedCount} completed corrective actions`;
  }
  if (activeCount > 0) {
    return activeCount === 1 ? '1 active corrective action' : `${activeCount} active corrective actions`;
  }
  return 'Recorded';
}

export function resolveWorkplaceConditionFollowUpResult(
  source: WorkplaceConditionContextSource,
): string | null {
  const impactSummary = (
    source.workflowSummary?.impact_summary && typeof source.workflowSummary.impact_summary === 'object'
      ? source.workflowSummary.impact_summary
      : null
  ) as WorkflowImpactSummary | null;

  return resolveResultLabelDisplay(
    deriveResultLabel({
      safetyCaseStatus: source.safetyCaseStatus,
      measurementStatus: source.measurementStatus,
      impactSummary,
      followupMeasurement: source.followupMeasurement,
      scope: source.scope || undefined,
    }),
  );
}

function buildContextRows(context: Omit<WorkplaceConditionContext, 'rows'>): WorkplaceConditionContextRow[] {
  return [
    { key: 'zone', label: 'Zone', value: context.zone || 'Not recorded' },
    { key: 'scan_window', label: 'Scan window', value: context.scanWindow || 'Not recorded' },
    { key: 'recurrence', label: 'Recurrence', value: context.recurrence || 'Not recorded' },
    { key: 'peak_time', label: 'Peak time', value: context.peakTime || 'Not recorded' },
    {
      key: 'corrective_action_status',
      label: 'Corrective action status',
      value: context.correctiveActionStatus || 'Not recorded',
    },
    {
      key: 'follow_up_result',
      label: 'Follow-up result',
      value: context.followUpResult || 'Pending',
    },
  ];
}

export function buildWorkplaceConditionContext(
  source: WorkplaceConditionContextSource,
): WorkplaceConditionContext {
  const partial = {
    zone: resolveWorkplaceConditionZone(source),
    scanWindow: formatWorkplaceConditionScanWindow(source.timeWindow),
    recurrence: resolveWorkplaceConditionRecurrence(source),
    peakTime: resolveWorkplaceConditionPeakTime(source),
    correctiveActionStatus: resolveWorkplaceConditionCorrectiveActionStatus(source),
    followUpResult: resolveWorkplaceConditionFollowUpResult(source),
  };
  return {
    ...partial,
    rows: buildContextRows(partial),
  };
}

export function buildWorkplaceConditionContextFromSafetyCase(
  item: SafetyCaseRecord,
  workflowSummary?: Record<string, unknown> | null,
): WorkplaceConditionContext {
  const followup = (
    (item as Record<string, unknown>).followup_measurement
    || item.status_snapshot?.followup_measurement
    || {}
  ) as Record<string, unknown>;

  return buildWorkplaceConditionContext({
    zoneLabel: item.zone_label,
    zoneId: item.zone_id,
    areaLabel: item.area_label,
    scope: item.scope as Record<string, unknown> | null,
    timeWindow: item.time_window,
    linkedReportCount: item.linked_report_count,
    logicalScanCount: (item as Record<string, unknown>).logical_scan_count as number | null,
    workflowSummary,
    statusSnapshot: item.status_snapshot,
    correctiveResponseStatus: item.corrective_response_status,
    safetyCaseStatus: item.safety_case_status,
    measurementStatus: item.measurement_status,
    followupMeasurement: followup,
  });
}

export function resolvePeakPeriodFromTimePatternVisual(
  visual?: { data?: { items?: Array<{ label?: unknown; value?: unknown }> } } | null,
): string | null {
  const items = visual?.data?.items;
  if (!Array.isArray(items) || !items.length) return null;
  let best = items[0];
  for (const item of items) {
    if (Number(item?.value || 0) > Number(best?.value || 0)) {
      best = item;
    }
  }
  const label = String(best?.label || '').trim();
  const value = Number(best?.value || 0);
  if (!label || !Number.isFinite(value) || value <= 0) return null;
  return label;
}

function readRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? value as Record<string, unknown> : {};
}

export function buildWorkplaceConditionContextFromArtifact(input: {
  persistedReport?: {
    report_payload?: Record<string, unknown> | null;
    report_content?: Record<string, unknown> | null;
    finalized_payload?: Record<string, unknown> | null;
    scope?: Record<string, unknown> | null;
    time_window?: Record<string, unknown> | null;
    workflow_anchor?: unknown;
    workflow_summary?: Record<string, unknown> | null;
    technical_details?: Record<string, unknown> | null;
  } | null;
  artifactScope?: Record<string, unknown> | null;
  artifactTimeWindow?: Record<string, unknown> | null;
  artifactAreaLabel?: string | null;
  buyerReport?: {
    executive_summary?: {
      safety_finding?: {
        peak_period?: string | null;
        event_signals?: number | null;
        primary_hotspot?: string | null;
      } | null;
    } | null;
  } | null;
  timePatternVisual?: { data?: { items?: Array<{ label?: unknown; value?: unknown }> } } | null;
}): WorkplaceConditionContext {
  const content = input.persistedReport?.report_content
    || input.persistedReport?.finalized_payload
    || input.persistedReport?.report_payload;
  const workflowAnchor = readRecord(input.persistedReport?.workflow_anchor);
  const anchorScope = readRecord(workflowAnchor.scope);
  const scope = (
    (content && typeof content === 'object' ? (content as Record<string, unknown>).scope : null)
    || input.persistedReport?.scope
    || input.artifactScope
    || anchorScope
    || {}
  ) as Record<string, unknown>;
  const timeWindow = (
    (content && typeof content === 'object' ? (content as Record<string, unknown>).time_window : null)
    || input.persistedReport?.time_window
    || input.artifactTimeWindow
    || workflowAnchor.time_window
    || scope.time_window
  ) as Record<string, unknown> | null;
  const technicalDetails = (
    input.persistedReport?.technical_details && typeof input.persistedReport.technical_details === 'object'
      ? input.persistedReport.technical_details
      : {}
  ) as Record<string, unknown>;
  const customerSummary = (
    technicalDetails.customer_summary && typeof technicalDetails.customer_summary === 'object'
      ? technicalDetails.customer_summary
      : {}
  ) as Record<string, unknown>;
  const customerEvidenceSummary = (
    technicalDetails.customer_evidence_summary && typeof technicalDetails.customer_evidence_summary === 'object'
      ? technicalDetails.customer_evidence_summary
      : {}
  ) as Record<string, unknown>;
  const reportCounts = (
    customerEvidenceSummary.report_counts && typeof customerEvidenceSummary.report_counts === 'object'
      ? customerEvidenceSummary.report_counts
      : {}
  ) as Record<string, unknown>;
  const buyerFinding = input.buyerReport?.executive_summary?.safety_finding;
  const primaryHotspot = String(buyerFinding?.primary_hotspot || '').trim() || null;
  const artifactAreaLabel = String(input.artifactAreaLabel || '').trim() || null;
  const workflowSummary = (
    input.persistedReport?.workflow_summary
    || (content && typeof content === 'object'
      ? (content as Record<string, unknown>).workflow_summary
      : null)
  ) as Record<string, unknown> | null;
  const mergedScope: Record<string, unknown> = {
    ...scope,
    ...(
      content && typeof content === 'object' && (content as Record<string, unknown>).result_status
        ? { result_status: (content as Record<string, unknown>).result_status }
        : {}
    ),
  };

  return buildWorkplaceConditionContext({
    zoneLabel: String(
      mergedScope.zone_label
      || mergedScope.area_label
      || primaryHotspot
      || artifactAreaLabel
      || '',
    ).trim() || null,
    zoneId: String(mergedScope.zone_id || '').trim() || null,
    areaLabel: String(mergedScope.area_label || primaryHotspot || artifactAreaLabel || '').trim() || null,
    scope: mergedScope,
    timeWindow,
    workflowSummary,
    correctiveActionStatusText: String(customerSummary.corrective_action_status_text || '').trim() || null,
    recurrenceSignalCount: toCount(reportCounts.related_recurrence_signals_lookback) ?? undefined,
    recurrenceEventCount: toCount(
      buyerFinding?.event_signals ?? reportCounts.selected_incidents ?? reportCounts.alerts_events_observed,
    ) ?? undefined,
    peakPeriod: buyerFinding?.peak_period
      || resolvePeakPeriodFromTimePatternVisual(input.timePatternVisual),
    safetyCaseStatus: String(mergedScope.safety_case_status || workflowSummary?.safety_case_status || '').trim() || null,
    followupMeasurement: (
      mergedScope.followup_measurement && typeof mergedScope.followup_measurement === 'object'
        ? mergedScope.followup_measurement
        : null
    ) as Record<string, unknown> | null,
  });
}

export function hasWorkplaceConditionContextContent(
  context?: WorkplaceConditionContext | null,
): boolean {
  if (!context) return false;
  return context.rows.some((row) => row.value && row.value !== 'Not recorded' && row.value !== 'Pending');
}
